import type { DataIssue, Frequency, IndicatorSeries } from "../types";

const DEFAULT_STALE_DAYS: Record<Frequency, number> = {
  daily: 14,
  weekly: 35,
  monthly: 95,
  quarterly: 220,
  annual: 550
};

function lastNumericDate(series: IndicatorSeries) {
  return [...series.observations].reverse().find((o) => o.value != null)?.date;
}

function dayAge(date: string, nowMs: number) {
  const t = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  return Math.floor((nowMs - t) / 86_400_000);
}

export function applyFreshness(
  series: IndicatorSeries,
  options: { nowMs?: number; staleAfterDays?: number } = {}
): { series: IndicatorSeries; issue?: DataIssue } {
  if (series.source.quality !== "official") return { series };
  const observationDate = series.source.observationDate ?? lastNumericDate(series);
  if (!observationDate) return { series };
  const maxAge = options.staleAfterDays ?? DEFAULT_STALE_DAYS[series.source.frequency];
  const age = dayAge(observationDate, options.nowMs ?? Date.now());
  if (age == null || age <= maxAge) return { series };

  const stale: IndicatorSeries = {
    ...series,
    source: {
      ...series.source,
      quality: "stale",
      observationDate,
      note: [series.source.note, `Freshness check: last numeric observation is ${age} days old; threshold=${maxAge} days.`]
        .filter(Boolean)
        .join(" ")
    }
  };
  return {
    series: stale,
    issue: {
      id: `${series.id}-stale`,
      severity: "warning",
      scope: series.id,
      message: `마지막 숫자 관측일 ${observationDate} (${age}일 전) — ${series.source.frequency} freshness 기준 ${maxAge}일을 초과해 STALE로 표시합니다.`
    }
  };
}

export function isUsableSeries(series: IndicatorSeries | undefined) {
  return Boolean(series && (series.source.quality === "official" || series.source.quality === "stale") && series.observations.some((o) => o.value != null));
}
